// frontend/src/components/dashboard/GraficoMovimento12m.jsx
import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import { Activity } from 'lucide-react';

/* =======================
   Paleta (Aurora)
======================= */
const AURORA = {
  grid: 'rgba(255,255,255,0.08)',
  text: 'rgba(255,255,255,0.90)',
  text2: 'rgba(255,255,255,0.65)',
  text3: 'rgba(255,255,255,0.45)',
  border: 'rgba(255,255,255,0.10)',

  // ✅ mesmo “fundo premium” usado no Financeiro
  cardBg: 'rgba(9,9,11,0.80)',

  // linhas
  l1: '#34D399', // emerald
  l2: '#A78BFA', // purple
  l3: '#60A5FA', // blue
  l4: '#FBBF24', // amber
  l5: '#F472B6', // pink
};

function formatInt(v) {
  const n = Number(v || 0);
  return Number.isFinite(n) ? n.toLocaleString('pt-BR') : '0';
}

function getLabelAno(year) {
  const y = Number(year);
  return Number.isFinite(y) ? String(y) : '—';
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  const items = [...payload].sort(
    (a, b) => Number(b.value || 0) - Number(a.value || 0)
  );

  return (
    <div className="rounded-2xl border border-white/10 bg-[#060b12]/95 p-3 text-xs text-white shadow-xl backdrop-blur">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="font-semibold text-white/90">{label}</div>
        <div className="text-[11px] text-white/50">mensal</div>
      </div>

      <div className="space-y-1.5">
        {items.map((p) => (
          <div
            key={p.dataKey}
            className="flex items-center justify-between gap-6"
          >
            <div className="flex items-center gap-2">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ background: p.color }}
              />
              <span className="text-white/75">{p.name}</span>
            </div>
            <span className="font-semibold text-white">
              {formatInt(p.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* =======================
   Mini-legenda (padrão da página)
======================= */
function LegendPills({ items = [] }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it) => (
        <div
          key={it.key}
          className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-white/75"
          title={it.label}
        >
          <span
            className="h-2 w-2 rounded-full"
            style={{ background: it.color }}
          />
          <span className="font-medium text-white/80">{it.short}</span>
          <span className="text-white/45">•</span>
          <span className="font-semibold text-white">
            {formatInt(it.total)}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Props:
 * - loading: boolean
 * - dataMensal: Array<{ mes, placas, imoveis, negocios, visitas, vendas }>
 * - year: number
 * - rightSlot: ReactNode (opcional)
 */
export default function GraficoMovimento12m({
  loading = false,
  dataMensal = [],
  year,
  rightSlot = null,
}) {
  const data = useMemo(() => {
    const arr = Array.isArray(dataMensal) ? dataMensal : [];
    return arr.map((m) => ({
      mes: m.mes, // Jan/Fev...
      placas: Number(m.placas || 0),
      imoveis: Number(m.imoveis || 0),
      negocios: Number(m.negocios || 0),
      visitas: Number(m.visitas || 0),
      vendas: Number(m.vendas || 0),
    }));
  }, [dataMensal]);

  const totals = useMemo(() => {
    const sum = (k) => data.reduce((acc, it) => acc + (Number(it[k]) || 0), 0);
    return {
      placas: sum('placas'),
      imoveis: sum('imoveis'),
      negocios: sum('negocios'),
      visitas: sum('visitas'),
      vendas: sum('vendas'),
    };
  }, [data]);

  const legendItems = useMemo(
    () => [
      {
        key: 'placas',
        label: 'Placas',
        short: 'Placas',
        color: AURORA.l4,
        total: totals.placas,
      },
      {
        key: 'imoveis',
        label: 'Imóveis',
        short: 'Imóveis',
        color: AURORA.l1,
        total: totals.imoveis,
      },
      {
        key: 'negocios',
        label: 'Negócios',
        short: 'Negócios',
        color: AURORA.l2,
        total: totals.negocios,
      },
      {
        key: 'visitas',
        label: 'Visitas',
        short: 'Visitas',
        color: AURORA.l3,
        total: totals.visitas,
      },
      {
        key: 'vendas',
        label: 'Vendas',
        short: 'Vendas',
        color: AURORA.l5,
        total: totals.vendas,
      },
    ],
    [totals]
  );

  return (
    // ✅ Agora com o MESMO “peso” visual do Financeiro
    <div className="relative h-full rounded-2xl border border-white/10 bg-zinc-950/80 p-4 shadow-2xl backdrop-blur">
      {/* halos aurora (sutil, igual sensação do financeiro) */}
      <div
        className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full blur-3xl"
        style={{ background: 'rgba(167,139,250,0.18)' }}
      />
      <div
        className="pointer-events-none absolute -left-12 -bottom-12 h-52 w-52 rounded-full blur-3xl"
        style={{ background: 'rgba(52,211,153,0.14)' }}
      />

      {/* Header */}
      <div className="relative mb-3 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5">
            <Activity size={18} className="text-white/80" />
          </div>

          <div>
            <div className="text-sm font-semibold text-white">
              Movimento & Resultado
            </div>

            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-white/60">
              <span>Histórico anual (Jan–Dez)</span>
              <span className="text-white/35">•</span>
              <span className="font-semibold text-white/75">
                {getLabelAno(year)}
              </span>
            </div>
          </div>
        </div>

        {rightSlot ? <div className="shrink-0">{rightSlot}</div> : null}
      </div>

      {/* Pills */}
      <div className="relative mb-3">
        <LegendPills items={legendItems} />
      </div>

      {/* ✅ Canvas do chart com o MESMO FUNDO do Financeiro */}
      <div
        className="relative h-[260px] w-full overflow-hidden rounded-2xl border border-white/10"
        style={{ background: AURORA.cardBg }}
      >
        {/* brilho leve no topo (igual vibe do financeiro) */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-emerald-400/60 via-violet-400/50 to-transparent" />

        {loading ? (
          <div className="h-full w-full animate-pulse bg-white/5" />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data}
              margin={{ top: 10, right: 10, bottom: 0, left: -10 }}
            >
              <CartesianGrid stroke={AURORA.grid} strokeDasharray="4 6" />

              <XAxis
                dataKey="mes"
                tick={{ fill: AURORA.text3, fontSize: 11 }}
                axisLine={{ stroke: AURORA.border }}
                tickLine={{ stroke: AURORA.border }}
                interval={0}
              />

              <YAxis
                tick={{ fill: AURORA.text3, fontSize: 11 }}
                axisLine={{ stroke: AURORA.border }}
                tickLine={{ stroke: AURORA.border }}
                allowDecimals={false}
              />

              <Tooltip content={<CustomTooltip />} />

              <Line
                type="monotone"
                dataKey="imoveis"
                name="Imóveis"
                stroke={AURORA.l1}
                strokeWidth={2.25}
                dot={false}
                activeDot={{ r: 4 }}
                isAnimationActive
              />
              <Line
                type="monotone"
                dataKey="negocios"
                name="Negócios"
                stroke={AURORA.l2}
                strokeWidth={2.25}
                dot={false}
                activeDot={{ r: 4 }}
                isAnimationActive
              />
              <Line
                type="monotone"
                dataKey="visitas"
                name="Visitas"
                stroke={AURORA.l3}
                strokeWidth={2.25}
                dot={false}
                activeDot={{ r: 4 }}
                isAnimationActive
              />
              <Line
                type="monotone"
                dataKey="placas"
                name="Placas"
                stroke={AURORA.l4}
                strokeWidth={2.25}
                dot={false}
                activeDot={{ r: 4 }}
                isAnimationActive
              />
              <Line
                type="monotone"
                dataKey="vendas"
                name="Vendas"
                stroke={AURORA.l5}
                strokeWidth={2.25}
                dot={false}
                activeDot={{ r: 4 }}
                isAnimationActive
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
